import React, { useEffect, useMemo, useState } from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import { DS } from '@/design-system'
import { getDSColorWithOpacity } from '@/design-system/Palette'
import { useBlindTimer } from '@/hooks/convex/useBlindTimer'
import { BLIND_LEVELS } from '@ppwr/convex/shared/tournamentConstants'
import { BlindScheduleDrawer } from '@/screens/shared/BlindScheduleDrawer'
import { useGameState } from './useGameState'

/**
 * Non-tournament HUD overlay shown above the table.
 * Mirrors the tournament HUD layout but uses hand number instead of table info.
 */
export function GameHUD() {
  const { game } = useGameState()
  const blindTimer = useBlindTimer(game ?? null)
  const [isBlindScheduleVisible, setIsBlindScheduleVisible] = useState(false)
  const [scheduleNow, setScheduleNow] = useState(() => Date.now())
  const currentBigBlind = game?.bigBlind ?? BLIND_LEVELS[0]
  const isBlindIncreaseEnabled = (game?.timeBetweenBlindsIncrease ?? 0) > 0
  const blindLevels = BLIND_LEVELS as readonly number[]
  const blindLevelIndex = useMemo(() => {
    const exactIndex = blindLevels.indexOf(currentBigBlind)
    if (exactIndex >= 0) {
      return exactIndex
    }

    const nextIndex = blindLevels.findIndex((level) => level > currentBigBlind)
    return nextIndex === -1 ? BLIND_LEVELS.length - 1 : Math.max(0, nextIndex - 1)
  }, [currentBigBlind, blindLevels])

  useEffect(() => {
    if (!isBlindScheduleVisible || !isBlindIncreaseEnabled || game?.mode === 'mtt') {
      return
    }

    setScheduleNow(Date.now())
    const interval = setInterval(() => setScheduleNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [isBlindScheduleVisible, isBlindIncreaseEnabled, game?.mode])

  const timeBetweenBlindsIncrease = game?.timeBetweenBlindsIncrease ?? 0
  const previousBlindIncreaseTimestamp = game?.previousBlindIncreaseTimestamp ?? 0
  const blindIncreaseOffset = game?.blindIncreaseOffset ?? 0
  const pausedAt = game?.pausedAt
  const remainingUntilNextBlindMs = useMemo(() => {
    if (!isBlindIncreaseEnabled || timeBetweenBlindsIncrease <= 0) {
      return null
    }

    const now = pausedAt || scheduleNow
    const effectiveElapsed = now - previousBlindIncreaseTimestamp - blindIncreaseOffset
    return Math.max(0, timeBetweenBlindsIncrease - effectiveElapsed)
  }, [isBlindIncreaseEnabled, timeBetweenBlindsIncrease, pausedAt, scheduleNow, previousBlindIncreaseTimestamp, blindIncreaseOffset])

  if (!game || game.mode === 'mtt') {
    return null
  }

  const nextBigBlind = game.nextBigBlind
  const nextSmallBlind = nextBigBlind ? Math.floor(nextBigBlind / 2) : 0
  const scheduleRows = blindLevels.map((bb, i) => {
    const msUntilLevel = i < blindLevelIndex
      ? null
      : i === blindLevelIndex
        ? 0
        : (remainingUntilNextBlindMs ?? 0) + (i - blindLevelIndex - 1) * timeBetweenBlindsIncrease

    const leftLabel = msUntilLevel == null
      ? '--'
      : i === blindLevelIndex
        ? 'Now'
        : msUntilLevel <= 0
          ? 'Next'
          : `${Math.ceil(msUntilLevel / 60_000)}m`

    return {
      bigBlind: bb,
      smallBlind: Math.floor(bb / 2),
      isCurrent: i === blindLevelIndex,
      isPast: i < blindLevelIndex,
      leftLabel
    }
  })
  const openBlindSchedule = () => setIsBlindScheduleVisible(true)

  return (
    <>
      <View style={styles.hudWrapper} pointerEvents="box-none">
        <View style={styles.hudContainer}>
          <DS.Text variant="Mini/Bold" style={styles.hudText}>
            Blinds: {game.smallBlind}/{game.bigBlind}
          </DS.Text>
          {isBlindIncreaseEnabled && nextBigBlind ? (
            <Pressable onPress={openBlindSchedule}>
              <DS.Text variant="Mini/Regular" style={[styles.hudSubtext, styles.tabularNums]}>
                Next: {nextSmallBlind}/{nextBigBlind} in {blindTimer}
              </DS.Text>
            </Pressable>
          ) : null}
          <View style={styles.hudDivider} />
          <DS.Text variant="Mini/Bold" style={styles.hudText}>
            Hand #{game.handNumber || 1}
          </DS.Text>
        </View>
      </View>
      <BlindScheduleDrawer
        visible={isBlindScheduleVisible}
        rows={scheduleRows}
        onClose={() => setIsBlindScheduleVisible(false)}
      />
    </>
  )
}

const styles = StyleSheet.create({
  hudWrapper: {
    position: 'absolute',
    bottom: 270,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 50
  },
  hudContainer: {
    alignItems: 'center',
    backgroundColor: getDSColorWithOpacity('Global/Black', 0.25),
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minWidth: 150,
    gap: 2
  },
  hudDivider: {
    height: 1,
    alignSelf: 'stretch',
    backgroundColor: getDSColorWithOpacity('Global/White', 0.2)
  },
  hudText: {
    color: DS.palette['Global/White']
  },
  hudSubtext: {
    color: DS.palette['Brand/Light Grey']
  },
  tabularNums: {
    fontVariant: ['tabular-nums']
  }
})
